import { useState } from "react";
import type { Theme } from "../lib/theme";

interface ChecklistProps {
  T: Theme;
  FONT: string;
  hasProjectName: boolean;
  hasLineItem: boolean;
  hasPackage: boolean;
  hasMarkup: boolean;
  onDismiss: () => void;
}

const STEP_LABELS: { key: keyof Omit<ChecklistProps, "T"|"FONT"|"onDismiss">; label: string; hint: string }[] = [
  { key: "hasProjectName", label: "Name your project", hint: "Click the project name in the top bar" },
  { key: "hasLineItem",    label: "Add a line item",   hint: "Pick a division → enter qty & unit cost" },
  { key: "hasPackage",     label: "Assign a package",  hint: "Set the Package column on any line item" },
  { key: "hasMarkup",      label: "Set overhead & profit", hint: "Use the % fields in the totals footer" },
];

export default function GetStartedChecklist({ T, FONT, hasProjectName, hasLineItem, hasPackage, hasMarkup, onDismiss }: ChecklistProps) {
  const [expanded, setExpanded] = useState(true);
  const values = { hasProjectName, hasLineItem, hasPackage, hasMarkup };
  const done = STEP_LABELS.filter(s => values[s.key]).length;
  const allDone = done === STEP_LABELS.length;

  return (
    <div style={{
      margin: "8px 6px 4px", borderRadius: 6,
      background: allDone ? T.bgAccentFaint : T.bgPanel,
      border: `1px solid ${allDone ? T.borderAccent + "55" : T.borderPrimary}`,
      overflow: "hidden", flexShrink: 0,
    }}>
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          padding: "7px 8px 6px", display: "flex", alignItems: "center", gap: 6,
          fontFamily: FONT, color: T.textSecondary,
        }}
      >
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: ".08em",
          color: allDone ? T.borderAccent : T.textSubLabel, flex: 1, textAlign: "left",
        }}>
          {allDone ? "✓ ALL SET" : "GET STARTED"}
        </span>
        <span style={{ fontSize: 10, color: T.textMuted }}>{done}/{STEP_LABELS.length}</span>
        <span style={{ fontSize: 10, color: T.textWeak, opacity: .6 }}>{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div style={{ padding: "0 8px 8px" }}>
          {STEP_LABELS.map(step => {
            const checked = values[step.key] as boolean;
            return (
              <div key={step.key} style={{ display: "flex", alignItems: "flex-start", gap: 7, marginBottom: 6 }}>
                <span style={{
                  width: 13, height: 13, borderRadius: 3, flexShrink: 0, marginTop: 1,
                  border: `1.5px solid ${checked ? T.borderAccent : T.borderSecondary}`,
                  background: checked ? T.borderAccent : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 10, color: "#fff", fontWeight: 700,
                }}>
                  {checked ? "✓" : ""}
                </span>
                <div>
                  <div style={{
                    fontSize: 11, fontFamily: FONT, lineHeight: 1.3,
                    color: checked ? T.textMuted : T.textSecondary,
                    textDecoration: checked ? "line-through" : "none",
                  }}>
                    {step.label}
                  </div>
                  {!checked && (
                    <div style={{ fontSize: 10, color: T.textWeak, lineHeight: 1.3, marginTop: 1 }}>
                      {step.hint}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {allDone && (
            <button onClick={onDismiss} style={{
              marginTop: 4, width: "100%", background: "none", border: `1px solid ${T.borderPrimary}`,
              borderRadius: 3, padding: "4px 0", fontSize: 10, color: T.textMuted,
              cursor: "pointer", fontFamily: FONT,
            }}>
              Dismiss
            </button>
          )}
        </div>
      )}
    </div>
  );
}
